/**
 * Product Board Page
 *
 * Kanban of product items across lifecycle stages. Cards are freely draggable
 * across all stages (including Deprecated), and vertically within a stage
 * (position persisted via sortOrder). Deprecated column is hidden unless
 * toggled on. Supports search + priority + customer filtering via the unified
 * board toolbar.
 *
 * @module features/product-board/pages/ProductBoardPage
 */

import React from 'react';
import { useNavigate } from '@tanstack/react-router';
import {
  DrawerTrigger,
  Button as HiddenButton,
  ToolbarFilterMenu,
  type MultiSelectOption,
  type FilterToolbarChip,
} from '@chatsdet/ui';
import { BOARDS } from '@chatsdet/cards-plugins';
import { boardsProductsRoute } from '../../../router';
import { Board } from '../../../ui/components/board';
import type { BoardColumnConfig, BoardDropEvent } from '../../../ui/components/board';
import { BoardToolbar, boardSurfaceClassName } from '../../boards/BoardToolbar';
import { useProductItems, useMoveProductItem } from '../hooks/useProductItems';
import { useCards } from '../../cards/hooks/useCards';
import {
  ACTIVE_STAGES,
  ALL_STAGES,
  PRIORITY_LABELS,
  STAGE_ACCENT,
  STAGE_DROP_TINT,
  STAGE_LABELS,
} from '../constants';
import type { ProductItem, ProductItemPriority, ProductItemStage } from '../types';
import { ProductItemCard, type CardMetricPreview } from '../components/ProductItemCard';
import { CreateProductItemDialog } from '../components/CreateProductItemDialog';
import { ProductItemDrawer } from '../components/ProductItemDrawer';
import { useAuth } from '../../../contexts/AuthContext';
import { useCardSparklines } from '../hooks/useProductCardMetrics';
import {
  buildProductBoardTelemetrySnapshot,
  recordProductBoardEventTelemetry,
  recordProductBoardRenderTelemetry,
} from '../productBoardTelemetry';

const PRIORITY_FILTER_OPTIONS: MultiSelectOption[] = (
  ['p0', 'p1', 'p2', 'p3'] as ProductItemPriority[]
).map((p) => ({ id: p, label: PRIORITY_LABELS[p] }));

export function ProductBoardPage() {
  const navigate = useNavigate();
  // Use the route's validated search (not raw location.search) so array params
  // like `priority` are parsed by validateSearch/parseSearchArray.
  const search = boardsProductsRoute.useSearch();

  const includeDeprecated = search.includeDeprecated === true;
  const activeItemId = search.itemId ?? null;
  const searchText = search.search ?? '';
  const priorityIds = React.useMemo(() => search.priority ?? [], [search.priority]);
  const priorities = priorityIds as ProductItemPriority[];
  const customer = search.customer || undefined;
  const supportsSearch = BOARDS.product_item.features.includes('search');

  const { data: items = [], isLoading } = useProductItems(includeDeprecated, {
    search: searchText,
    priorities,
    accountCardId: customer,
  });
  const moveItem = useMoveProductItem(includeDeprecated);

  // Batched per-card metric sparklines (superadmin only). One request decorates
  // every card; we pick each card's most-recent (first) linked metric.
  const { user } = useAuth();
  const isSuperadmin = user?.isSuperadmin === true;
  const { data: sparklines } = useCardSparklines(isSuperadmin);
  const metricPreviewByCard = React.useMemo(() => {
    const map = new Map<string, CardMetricPreview>();
    for (const row of sparklines ?? []) {
      if (!map.has(row.cardId) && row.values.length > 0) {
        map.set(row.cardId, {
          metricName: row.metricName,
          unit: row.unit,
          values: row.values,
        });
      }
    }
    return map;
  }, [sparklines]);

  const { data: accounts = [] } = useCards('account');
  const accountOptions = React.useMemo<MultiSelectOption[]>(
    () => accounts.map((account) => ({ id: account.id, label: account.title })),
    [accounts]
  );

  const visibleStages: ProductItemStage[] = includeDeprecated ? ALL_STAGES : ACTIVE_STAGES;

  const itemsByColumn = React.useMemo(() => {
    const map: Record<ProductItemStage, ProductItem[]> = {
      ideas: [],
      prioritized: [],
      in_development: [],
      dogfooding: [],
      beta: [],
      on_hold: [],
      ga: [],
      internal: [],
      deprecated: [],
    };
    for (const it of items) map[it.stage].push(it);
    // Items come back pre-sorted by stage + sortOrder; keep as-is.
    return map;
  }, [items]);

  const columns: BoardColumnConfig<ProductItemStage>[] = visibleStages.map((stage) => ({
    id: stage,
    label: STAGE_LABELS[stage],
    accentClass: STAGE_ACCENT[stage],
    dropClass: STAGE_DROP_TINT[stage],
    isLoading,
    totalItems: itemsByColumn[stage].length,
    emptyLabel: 'No items',
  }));

  const productBoardTelemetry = React.useMemo(
    () =>
      buildProductBoardTelemetrySnapshot({
        includeDeprecated,
        searchText,
        priorityIds,
        customerId: customer ?? null,
        activeItemId,
        mutationStatus: moveItem.status,
        mutationPending: moveItem.isPending,
        isLoading,
        visibleStages,
        itemsByColumn,
      }),
    [
      includeDeprecated,
      searchText,
      priorityIds,
      customer,
      activeItemId,
      moveItem.status,
      moveItem.isPending,
      isLoading,
      visibleStages,
      itemsByColumn,
    ]
  );

  React.useEffect(() => {
    recordProductBoardRenderTelemetry(productBoardTelemetry);
  }, [productBoardTelemetry]);

  const validateProductDrop = React.useCallback(
    (from: ProductItemStage, to: ProductItemStage, item: ProductItem) =>
      from === to || !item.flagDriven,
    []
  );

  const handleDrop = (event: BoardDropEvent<ProductItemStage, ProductItem>) => {
    moveItem.mutate({
      id: event.item.id,
      stage: event.toColumnId,
      beforeItemId: event.beforeItemId,
      afterItemId: event.afterItemId,
    });
  };

  const handleCardClick = (item: ProductItem) => {
    void navigate({
      to: '/boards/products',
      search: { ...search, itemId: item.id },
    });
  };

  const handleDrawerOpenChange = (open: boolean) => {
    if (!open) {
      void navigate({
        to: '/boards/products',
        search: { ...search, itemId: undefined },
      });
    }
  };

  const handleSearchChange = (value: string) => {
    void navigate({
      to: '/boards/products',
      search: {
        ...search,
        search: value.trim().length > 0 ? value : undefined,
      },
    });
  };

  const filterChips = React.useMemo<FilterToolbarChip[]>(() => {
    const chips: FilterToolbarChip[] = [];
    if (includeDeprecated) chips.push({ id: 'deprecated', label: 'Include deprecated' });
    for (const id of priorityIds) {
      chips.push({ id: `priority:${id}`, label: PRIORITY_LABELS[id as ProductItemPriority] ?? id });
    }
    if (customer) {
      const account = accountOptions.find((a) => a.id === customer);
      chips.push({ id: `customer:${customer}`, label: account?.label ?? 'Customer' });
    }
    return chips;
  }, [includeDeprecated, priorityIds, customer, accountOptions]);

  const handleChipRemove = (chipId: string) => {
    if (chipId === 'deprecated') {
      void navigate({
        to: '/boards/products',
        search: { ...search, includeDeprecated: undefined },
      });
      return;
    }
    if (chipId.startsWith('priority:')) {
      const removed = chipId.slice('priority:'.length);
      const next = priorityIds.filter((id: string) => id !== removed);
      void navigate({
        to: '/boards/products',
        search: { ...search, priority: next.length > 0 ? next : undefined },
      });
      return;
    }
    if (chipId.startsWith('customer:')) {
      void navigate({
        to: '/boards/products',
        search: { ...search, customer: undefined },
      });
    }
  };

  return (
    <>
      <div className="flex min-h-0 flex-1 flex-col">
        <BoardToolbar
          searchValue={searchText}
          onSearchChange={supportsSearch ? handleSearchChange : undefined}
          searchPlaceholder="Search product items"
          filters={
            <>
              <ToolbarFilterMenu
                label="Priority"
                options={PRIORITY_FILTER_OPTIONS}
                selectedIds={priorityIds}
                onChange={(ids) =>
                  void navigate({
                    to: '/boards/products',
                    search: { ...search, priority: ids.length > 0 ? ids : undefined },
                  })
                }
              />
              <ToolbarFilterMenu
                label="Customer"
                selectionMode="single"
                options={accountOptions}
                selectedIds={customer ? [customer] : []}
                onChange={(ids) =>
                  void navigate({
                    to: '/boards/products',
                    search: { ...search, customer: ids[0] },
                  })
                }
              />
              <ToolbarFilterMenu
                label="Deprecated"
                selectionMode="single"
                options={[{ id: 'include', label: 'Include deprecated' }]}
                selectedIds={includeDeprecated ? ['include'] : []}
                onChange={(ids) =>
                  void navigate({
                    to: '/boards/products',
                    search: { ...search, includeDeprecated: ids.length > 0 ? true : undefined },
                  })
                }
              />
            </>
          }
          actions={<CreateProductItemDialog includeDeprecated={includeDeprecated} />}
          chips={filterChips}
          onChipRemove={handleChipRemove}
        />

        <Board<ProductItemStage, ProductItem>
          className={boardSurfaceClassName}
          columns={columns}
          itemsByColumn={itemsByColumn}
          getItemId={(it) => it.id}
          getItemColumnId={(it) => it.stage}
          // Flag-linked cards have flag-driven stages: allow vertical reorder
          // (same column) but reject cross-column drops (invalid targets tint).
          validateDrop={validateProductDrop}
          onDrop={handleDrop}
          onTelemetry={(event) => recordProductBoardEventTelemetry(productBoardTelemetry, event)}
          onCardClick={handleCardClick}
          renderCard={(item, { isOverlay }) => (
            <ProductItemCard
              item={item}
              isOverlay={isOverlay}
              deprecated={item.stage === 'deprecated'}
              metricPreview={metricPreviewByCard.get(item.id)}
            />
          )}
        />
      </div>

      <DrawerTrigger isOpen={!!activeItemId} onOpenChange={handleDrawerOpenChange}>
        <HiddenButton className="hidden">Open</HiddenButton>
        {activeItemId ? (
          <ProductItemDrawer
            itemId={activeItemId}
            includeDeprecated={includeDeprecated}
            onClose={() => handleDrawerOpenChange(false)}
          />
        ) : (
          <div />
        )}
      </DrawerTrigger>
    </>
  );
}
